import React from "react";
import { Helmet } from "react-helmet";
import { ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";

const FAQ = () => {
  const navigate = useNavigate();

  const faqs = [
    {
      question: "What is the Recovery House Portal?",
      answer:
        "The Recovery House Portal is a comprehensive platform designed to help recovery home owners efficiently manage their operations. It includes features for tracking residents, managing payments and invoices, analyzing data, monitoring recovery capital, UA tracking, and providing residents with a personalized dashboard to support their recovery journey. This is just the beginning, as the platform offers many additional tools and capabilities to streamline operations and enhance recovery outcomes.",
    },
    {
      question: "Is my data safe?",
      answer:
        "Yes, your data is safe. All resident and user information is stored securely using advanced encryption protocols to protect data both at rest and in transit. We implement strict security measures and access controls to ensure that only authorized personnel can access sensitive information. Additionally, identifiable resident data will never be shared with anyone. Our platform is designed with privacy and security as top priorities, giving you peace of mind while using our services.",
    },
    {
      question: "Is there customer support available?",
      answer:
        "Yes! Customer support is always available to help you navigate the platform with quick response times. Our dedicated support team is here to ensure you have the best experience possible and to address any issues or questions you may have.",
    },
    {
      question: "How do I report a bug or request a feature?",
      answer:
        "You can reach out to our customer support team to report a bug or request a feature at recoveryhouseplatform@gmail.com. We value community feedback and actively encourage users to share their ideas for feature upgrades or additions. Your input helps us build the best platform possible.",
    },
    {
      question: "Are there different pricing plans?",
      answer:
        "Yes. The main portal is discounted for all recovery homes that can prove certification through their state NARR affiliate. Additionally, the resident dashboard is an optional add-on.",
    },
    {
      question: "What does it cost?",
      answer:
        "NARR-certified homes are $4.00 per bed and $1.00 per resident user, which provides access to the resident dashboard. This pricing model allows flexibility with home counts. Non-certified homes are $6.00 per bed and $1.50 per resident user.",
    },
    {
      question: "What is the Resident Dashboard and do I have to use it?",
      answer: (
        <div className="text-gray-400">
          <p className="text-gray-400">
            The Resident Dashboard is a feature-rich component designed to
            enhance the experience for both residents and recovery home owners.
            It includes:
          </p>
          <ul className="list-disc list-inside mt-2 space-y-2 text-gray-400">
            <li className="text-gray-400">Geo-Location meeting check-ins</li>
            <li className="text-gray-400">Overnight Requests</li>
            <li className="text-gray-400">Residents' Payment History</li>
            <li className="text-gray-400">Residents' UA History</li>
            <li className="text-gray-400">Residents' Recovery Capital Data</li>
            <li className="text-gray-400">
              Residents' Personalized Budget Planning
            </li>
            <li className="text-gray-400">Residents' Phase integration</li>
            <li className="text-gray-400">
              A schedule for home owners to track check-ins, overnights, and
              general meeting events
            </li>
            <li className="text-gray-400">And more..</li>
          </ul>
          <p className="mt-4 text-gray-400">
            The Resident Dashboard is completely optional. You can choose to use
            it based on your specific needs.
          </p>
        </div>
      ),
    },
    {
      question: "What kind of analytics can I see on the platform?",
      answer:
        "The platform offers analytics such as recovery times, average length of stay, probation/parole statistics, age demographics, sobriety times, medication and MAT data, referral data, discharge analytics, and recurrence analytics. We are always open to suggestions for additional data metrics that might interest you.",
    },
    {
      question: "Is the platform compatible with mobile devices?",
      answer:
        "Yes, everything that works on the desktop is fully compatible with mobile devices and is sized appropriately for seamless use on cell phones.",
    },
    {
      question: "Can you help me migrate data from another system?",
      answer:
        "We can assist with data migration depending on the structure of your current system. While some adjustments may be necessary to align with our platform’s structure, we recommend exporting your data in a CSV or SQL format for a smoother transition. Our team will work closely with you to ensure the migration process is as seamless as possible.",
    },
  ];

  return (
    <>
      <Helmet>
        <title>FAQ &bull; Recovery House Platform</title>
      </Helmet>

      <div className="min-h-screen bg-[#18181B] bg-gradient-to-br from-zinc-900 via-zinc-900/95 to-zinc-950 text-slate-300 flex flex-col items-center">
        <div className="max-w-4xl text-center px-6 py-12">
          <h1 className="text-5xl font-extrabold text-white mb-6">
            Frequently Asked Questions
          </h1>

          <p className="text-xl text-slate-300 mb-12">
            Explore answers to common questions about the Recovery House
            Platform. If you don’t find what you’re looking for, feel free to
            reach out to our support team for personalized assistance.
          </p>

          <button
            onClick={() => navigate("/")}
            className="px-8 py-3 bg-cyan-500 hover:bg-cyan-600 text-white rounded-full transition-all duration-300 mb-12"
          >
            Back to Welcome Page
          </button>

          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <details
                key={index}
                className="group bg-slate-900/50 backdrop-blur-xl rounded-lg p-6 border border-slate-800 hover:border-slate-700 transition-all duration-300"
              >
                <summary className="flex items-center justify-between cursor-pointer">
                  <h3 className="text-lg font-semibold text-white">
                    {faq.question}
                  </h3>
                  <ChevronDown className="text-slate-300 group-open:rotate-180 transition-transform duration-300" />
                </summary>
                <div className="mt-4 text-gray-400 text-left">{faq.answer}</div>
              </details>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default FAQ;
